import React, { forwardRef } from 'react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import { Root, Error } from './Autocomplete.style';

import { Icon } from '../Icon/Icon';

const getSelectComponent = (isCreatable, isAsync) => {
  if (isCreatable && isAsync) return AsyncCreatableSelect;
  if (isCreatable) return CreatableSelect;
  if (isAsync) return AsyncSelect;
  return Select;
};

export const Autocomplete = forwardRef(
  (
    {
      className,
      style,
      isCreatable = false,
      isAsync = false,
      readOnly,
      menuIsOpen,
      error,
      ...props
    },
    ref
  ) => {
    const SelectComponent = getSelectComponent(isCreatable, isAsync);

    return (
      <Root className={className} style={style}>
        <SelectComponent
          ref={ref}
          components={{
            ValueContainer,
            Input,
            ClearIndicator,
            DropdownIndicator,
            MultiValueRemove,
            IndicatorSeparator: () => null,
          }}
          menuIsOpen={readOnly ? false : menuIsOpen}
          styles={getCustomStyles(!!error?.message)}
          {...props}
        />

        {error && <Error>{error?.message}</Error>}
      </Root>
    );
  }
);

const ValueContainer = ({ children, ...props }) => (
  <components.ValueContainer {...props}>
    <components.Placeholder {...props} isFocused={props.isFocused}>
      {props.selectProps.placeholder}
    </components.Placeholder>
    {React.Children.map(children, (child) =>
      child && child.type !== components.Placeholder ? child : null
    )}
  </components.ValueContainer>
);

const Input = (props) => (
  <components.Input {...props} readOnly={props.selectProps.readOnly} />
);

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon
        icon="chevron-down"
        style={{ width: 20, height: 20, fontSize: 20 }}
      />
    </components.DropdownIndicator>
  );
};

const MultiValueRemove = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <Icon icon="x" style={{ width: 12, height: 12, fontSize: 12 }} />
    </components.MultiValueRemove>
  );
};

const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <button>
        <Icon icon="x" style={{ width: 16, height: 16, fontSize: 16 }} />
      </button>
    </components.ClearIndicator>
  );
};

const getCustomStyles = (hasErrors) => ({
  container: (provided) => ({
    ...provided,
    width: '100%',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    position: (state.hasValue || state.selectProps.inputValue) && 'absolute',
    top: (state.hasValue || state.selectProps.inputValue) && -15,
    padding: (state.hasValue || state.selectProps.inputValue) && '0 4px',
    backgroundColor: (state.hasValue || state.selectProps.inputValue) && '#fff',
    fontSize: state.hasValue || state.selectProps.inputValue ? 10 : 14,
    transition: 'top 150ms, font-size 150ms',
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 48,
    backgroundColor: 'transparent',
    fontSize: 14,
    letterSpacing: 0.14,
    borderColor: hasErrors
      ? '#cf2b23'
      : state.isFocused
      ? '#cf2b23'
      : '#dcdfe9',
    borderRadius: 5,
    boxShadow: 'none',
    outline: 'none',
    transition: 'all 150ms',
    '&:hover': {
      borderColor: hasErrors
        ? '#cf2b23'
        : state.isFocused
        ? '#cf2b23'
        : '#dcdfe9',
    },

    // iPhone and iOS Form Input Zoom Fixes
    '@media screen and (device-aspect-ratio: 2/3)': {
      fontSize: '16px',
    },
    '@media screen and (device-aspect-ratio: 40/71)': {
      fontSize: '16px',
    },
    '@media screen and (device-aspect-ratio: 375/667)': {
      fontSize: '16px',
    },
    '@media screen and (device-aspect-ratio: 9/16)': {
      fontSize: '16px',
    },
  }),
  input: (provided) => ({
    ...provided,
  }),
  singleValue: (provided) => ({
    ...provided,
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 12,
    overflow: 'visible',
  }),
  multiValue: (provided) => ({
    ...provided,
    alignItems: 'center',
    columnGap: 4,
    height: 24,
    padding: '0 8px',
    backgroundColor: '#8f95a5',
    borderRadius: 200,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    padding: 0,
    paddingLeft: 0,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 0,
  }),
  clearIndicator: (provided) => ({
    ...provided,
    justifyContent: 'center',
    alignItems: 'center',
    width: '16px',
    height: '16px',
    padding: 0,
    color: 'inherit',
  }),
  dropdownIndicator: (provided, { selectProps: { menuIsOpen } }) => ({
    ...provided,
    justifyContent: 'center',
    alignItems: 'center',
    width: 48,
    height: '100%',
    padding: 0,
    color: '#8f95a5',
    transform: menuIsOpen && 'rotate(180deg)',

    ':hover': {
      backgroundColor: 'transparent',
      opacity: 1,
    },
  }),
  menu: (provided) => ({
    ...provided,
    paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
    boxShadow: '0 10px 30px 0 rgba(0, 0, 0, 0.1)',
  }),
  menuList: (provided) => ({
    ...provided,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  menuPortal: (provided) => ({
    ...provided,
    fontFamily: 'ProductSans',
    zIndex: 8000,
  }),
  group: (provided) => ({
    ...provided,
    marginBottom: 10,
    padding: 0,
  }),
  groupHeading: (provided) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    height: 30,
    color: '#8f95a5',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.33,
    textTransform: 'uppercase',
    borderRadius: 4,
  }),
  option: (provided, { isFocused, isSelected, isDisabled }) => {
    const styles = {
      display: 'flex',
      alignItems: 'center',
      height: 32,
      padding: '0 16px',
      backgroundColor:
        (isFocused && '#f4f4f4') ||
        (isSelected && 'rgb(219, 223, 234)') ||
        'transparent',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1,
      opacity: isDisabled && 0.6,
      cursor: 'pointer',
    };
    return {
      ...provided,
      ...styles,
    };
  },
  multiValueRemove: (provided) => ({
    ...provided,
    justifyContent: 'center',
    width: 14,
    height: 14,
    color: '#fff',
    padding: 0,
    borderRadius: 0,

    '&:hover': {
      color: '#fff',
      backgroundColor: 'transparent',
      opacity: 0.5,
      cursor: 'pointer',
    },
  }),
});
